import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import FileUpload from '../components/FileUpload';
import EmailVerificationPanel from '../components/EmailVerificationPanel';
import SafeIcon from '../common/SafeIcon';
import { createEmailVerificationSession } from '../services/sessionService';

const LeadVerification = ({ onLeadsSelected, config, currentSessionId }) => {
  const [emails, setEmails] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [showVerificationPanel, setShowVerificationPanel] = useState(false);
  const [verificationSessionId, setVerificationSessionId] = useState(null);
  
  // Debug state changes
  useEffect(() => {
    console.log('🔍 LeadVerification state changed:');
    console.log('  - emails:', emails?.length || 0);
    console.log('  - rawData:', rawData?.length || 0);
    console.log('  - showVerificationPanel:', showVerificationPanel);
  }, [emails, rawData, showVerificationPanel]);
  
  // Handle emails extracted from CSV
  const handleEmailsExtracted = async (extractedEmails, extractedRawData) => {
    console.log('🔍 LeadVerification: handleEmailsExtracted called');
    console.log('📧 Emails extracted:', extractedEmails?.length || 0);
    console.log('📊 Raw data extracted:', extractedRawData?.length || 0);
    console.log('📧 First few emails:', extractedEmails?.slice(0, 3));
    
    // Create a new email verification session
    try {
      const sessionName = `Email Verification - ${new Date().toLocaleDateString()}`;
      const sessionResult = await createEmailVerificationSession(sessionName, extractedEmails?.length || 0);
      
      if (sessionResult.success) {
        console.log('Created email verification session:', sessionResult.sessionId);
        setVerificationSessionId(sessionResult.sessionId);
      } else {
        console.warn('Failed to create session, using fallback');
        setVerificationSessionId(currentSessionId || 'default');
      }
    } catch (error) {
      console.error('Error creating session:', error);
      setVerificationSessionId(currentSessionId || 'default');
    }
    
    setEmails(extractedEmails || []);
    setRawData(extractedRawData || []);
    setShowVerificationPanel(true);
    
    console.log('✅ Verification panel should now be visible');
  };
  
  // Handle verification completion
  const handleVerificationComplete = (verificationResults) => {
    console.log('Verification completed:', verificationResults);
  };

  // Handle selection of verified leads for analysis
  const handleLeadsSelected = (selectedLeads) => {
    console.log('Selected leads for analysis:', selectedLeads);
    
    // Convert leads to website format for analysis
    const websiteData = selectedLeads.map(lead => ({
      url: lead.url || lead.website || '', // Use 'url' property to match websiteAnalyzer expectations
      company: lead.company || '',
      email: lead.email || '',
      firstName: lead.first_name || '',
      lastName: lead.last_name || '',
      // Include additional data
      jobTitle: lead.job_title || '',
      industry: lead.industry || '',
      country: lead.country || ''
    })).filter(item => item.url); // Only include leads with URLs
    
    if (websiteData.length > 0) {
      // Pass selected leads to parent component
      onLeadsSelected(websiteData);
      
      // Redirect to dashboard for analysis
      window.dispatchEvent(new CustomEvent('navigate', { detail: { tab: 'dashboard' } }));
    } else {
      alert('No websites found in selected leads. Please ensure your leads have website information.');
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.1 }}
      className="lead-verification-container"
    >
      <div className="page-header">
        <h2 className="page-title">Lead Verification</h2>
        <p className="page-description">
          Upload a CSV file with email addresses to verify them before analysis.
        </p>
      </div>
      
      {!showVerificationPanel && (
        <div className="upload-section">
          <FileUpload 
            onEmailsExtracted={handleEmailsExtracted}
            onFileUpload={() => {}} // Dummy function since we're only using email extraction
            disabled={false}
            verifyEmails={true}
          />
        </div>
      )}
      
      {showVerificationPanel ? (
        <EmailVerificationPanel
          emails={emails}
          rawData={rawData}
          sessionId={verificationSessionId || currentSessionId || 'default'}
          onVerificationComplete={handleVerificationComplete}
          onLeadsSelected={handleLeadsSelected}
          config={config}
        />
      ) : (
        <div style={{display: 'none'}}>
          {console.log('🔍 showVerificationPanel is false, emails:', emails?.length, 'showVerificationPanel:', showVerificationPanel)}
        </div>
      )}
      
      <style jsx>{`
        .lead-verification-container {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          padding: 1rem;
        }
        
        .page-header {
          margin-bottom: 2rem;
        }
        
        .page-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        
        .page-description {
          color: var(--text-light);
        }
        
        .upload-section,
        .verification-section {
          margin-bottom: 2rem;
        }
      `}</style>
    </motion.div>
  );
};

export default LeadVerification;